import { Droppable } from '@hello-pangea/dnd';
import { makeStyles, Tooltip } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { memo, useCallback, useRef } from 'react';
import { CopyIcon } from '../../../assets/icons';
import { COLORS, MONTSERRAT_FONT_FAMILY } from '../../../theme/variables';
import HeatmapChip, { ChipData } from './heatmap-chip';
import { TooltipData } from './heatmap-tooltip';

interface BadgeInfo {
  label: string;
  color: 'green' | 'blue' | 'red' | 'gray';
}

interface HeatmapCardProps {
  index: number;
  entityName: string;
  badges: BadgeInfo[];
  assignedChips: ChipData[];
  candidateChips: ChipData[];
  approvedChips?: ChipData[];
  approvedLabel?: string;
  droppableId: string;
  onRemoveChip: (chipId: string) => void;
  onChipHover: (el: HTMLElement | null, data: TooltipData | null) => void;
  assignedLabel: string;
  candidateLabel: string;
  onAddClick?: () => void;
  addButtonLabel?: string;
  onSecondaryClick?: () => void;
  secondaryButtonLabel?: string;
  hideChipScores?: boolean;
  isDropDisabled?: boolean;
  noItemsLabel?: string;
  nameTooltipData?: TooltipData;
  voteLinkCopied?: boolean;
  onCopyVoteLink?: () => void;
  isFulfilled?: boolean;
}

const BADGE_COLORS: Record<BadgeInfo['color'], { bg: string; text: string }> = {
  green: {
    bg: COLORS.COLOR_GREEN_LIGHTENED_45,
    text: COLORS.COLOR_GREEN_BASE,
  },
  blue: {
    bg: COLORS.COLOR_BLUE_LIGHTENED_40,
    text: COLORS.COLOR_BLUE_BASE,
  },
  red: {
    bg: COLORS.COLOR_RED_LIGHTENED,
    text: COLORS.COLOR_RED_BASE,
  },
  gray: {
    bg: COLORS.COLOR_GRAY_LIGHTENED_40,
    text: COLORS.COLOR_GRAY_DARKENED,
  },
};

const useStyles = makeStyles({
  card: {
    border: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    borderRadius: 8,
    overflow: 'hidden',
    flexShrink: 0,
    transition: 'border-color 0.12s, background-color 0.12s',
  },
  cardDragOver: {
    borderColor: COLORS.COLOR_BLUE_BASE,
    backgroundColor: COLORS.COLOR_BLUE_LIGHTENED_50,
  },
  cardDragOverDisabled: {
    borderColor: COLORS.COLOR_RED_BASE,
    backgroundColor: COLORS.COLOR_RED_LIGHTENED,
  },
  cardFulfilled: {
    backgroundColor: COLORS.COLOR_GREEN_LIGHTENED_45,
    borderColor: COLORS.COLOR_GREEN_BASE,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    backgroundColor: COLORS.COLOR_ADDITIONAL_GENERAL_BACKGROUND,
  },
  headerName: {
    fontFamily: MONTSERRAT_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 14,
    color: COLORS.COLOR_TEXT_BASE,
    maxWidth: '70%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    cursor: 'default',
  },
  headerNameHoverable: {
    cursor: 'pointer',
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 10px',
    borderRadius: 12,
    whiteSpace: 'nowrap' as const,
  },
  body: {
    padding: '12px 16px',
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
    marginBottom: 8,
  },
  chipGroup: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 6,
    marginBottom: 12,
  },
  noItems: {
    fontSize: 13,
    color: COLORS.COLOR_GRAY_BASE,
    fontStyle: 'italic',
  },
  addButton: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 10px',
    borderRadius: 12,
    whiteSpace: 'nowrap' as const,
    border: `1px solid ${COLORS.COLOR_BLUE_BASE}`,
    backgroundColor: 'transparent',
    color: COLORS.COLOR_BLUE_BASE,
    cursor: 'pointer',
    transition: 'background-color 0.12s',
    '&:hover': {
      backgroundColor: COLORS.COLOR_BLUE_LIGHTENED_40,
    },
  },
  secondaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 10px',
    borderRadius: 12,
    whiteSpace: 'nowrap' as const,
    border: `1px solid ${COLORS.COLOR_RED_BASE}`,
    backgroundColor: 'transparent',
    color: COLORS.COLOR_RED_BASE,
    cursor: 'pointer',
    transition: 'background-color 0.12s',
    '&:hover': {
      backgroundColor: COLORS.COLOR_RED_LIGHTENED,
    },
  },
  voteLinkIcon: {
    color: COLORS.COLOR_GRAY_BASE,
    cursor: 'pointer',
    fontSize: 16,
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: 10,
    '&:hover': {
      color: COLORS.COLOR_BLUE_BASE,
    },
  },
  voteLinkSuccess: {
    fontSize: 16,
    color: COLORS.COLOR_GREEN_BASE,
    marginRight: 10,
  },
});

// No-op remove handler for locked (approved) chips
const noop = () => {};

const HeatmapCard = memo(function HeatmapCard({
  index,
  entityName,
  badges,
  assignedChips,
  candidateChips,
  approvedChips,
  approvedLabel,
  droppableId,
  onRemoveChip,
  onChipHover,
  assignedLabel,
  candidateLabel,
  onAddClick,
  addButtonLabel,
  onSecondaryClick,
  secondaryButtonLabel,
  hideChipScores,
  isDropDisabled,
  noItemsLabel,
  nameTooltipData,
  voteLinkCopied,
  onCopyVoteLink,
  isFulfilled,
}: HeatmapCardProps) {
  const classes = useStyles();
  const nameRef = useRef<HTMLSpanElement>(null);

  const handleNameMouseEnter = useCallback(() => {
    if (nameTooltipData && nameRef.current) {
      onChipHover(nameRef.current, nameTooltipData);
    }
  }, [onChipHover, nameTooltipData]);

  const handleNameMouseLeave = useCallback(() => {
    if (nameTooltipData) {
      onChipHover(null, null);
    }
  }, [onChipHover, nameTooltipData]);

  const approvedCount = approvedChips?.length ?? 0;
  const hasAny =
    assignedChips.length > 0 || candidateChips.length > 0 || approvedCount > 0;

  return (
    <Droppable droppableId={droppableId} isDropDisabled={isDropDisabled}>
      {(provided, snapshot) => {
        let cardModifier = '';
        if (isDropDisabled) {
          cardModifier = classes.cardDragOverDisabled;
        } else if (snapshot.isDraggingOver) {
          cardModifier = classes.cardDragOver;
        } else if (isFulfilled) {
          cardModifier = classes.cardFulfilled;
        }
        return (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`${classes.card} ${cardModifier}`}
            data-testid={`heatmap-card-${droppableId}`}
          >
            <div className={classes.header}>
              <span
                ref={nameRef}
                className={`${classes.headerName}${
                  nameTooltipData ? ` ${classes.headerNameHoverable}` : ''
                }`}
                title={entityName}
                onMouseEnter={handleNameMouseEnter}
                onMouseLeave={handleNameMouseLeave}
              >
                {index + 1}. {entityName}
              </span>
              <div className={classes.badgeRow}>
                {badges
                  .filter((b) => b.label)
                  .map((b) => {
                    const colors = BADGE_COLORS[b.color];
                    return (
                      <span
                        key={b.label}
                        className={classes.badge}
                        style={{
                          backgroundColor: colors.bg,
                          color: colors.text,
                        }}
                      >
                        {b.label}
                      </span>
                    );
                  })}
                {onCopyVoteLink &&
                  (voteLinkCopied ? (
                    <CheckCircleIcon
                      className={classes.voteLinkSuccess}
                      data-testid={`vote-link-copied-${droppableId}`}
                    />
                  ) : (
                    <Tooltip title='Copy vote link'>
                      <span
                        className={classes.voteLinkIcon}
                        onClick={onCopyVoteLink}
                        data-testid={`vote-link-${droppableId}`}
                      >
                        <CopyIcon />
                      </span>
                    </Tooltip>
                  ))}
                {onAddClick && (
                  <button
                    type='button'
                    className={classes.addButton}
                    onClick={() => onAddClick()}
                    data-testid={`heatmap-add-${droppableId}`}
                  >
                    {addButtonLabel || '+ Add'}
                  </button>
                )}
                {onSecondaryClick && (
                  <button
                    type='button'
                    className={classes.secondaryButton}
                    onClick={() => onSecondaryClick()}
                    aria-label={`${
                      secondaryButtonLabel || 'Remove'
                    } ${entityName}`}
                    data-testid={`heatmap-secondary-${droppableId}`}
                  >
                    {secondaryButtonLabel || 'Remove'}
                  </button>
                )}
              </div>
            </div>
            <div className={classes.body}>
              {!hasAny ? (
                <span className={classes.noItems}>
                  {noItemsLabel || 'No items'}
                </span>
              ) : (
                <>
                  {approvedCount > 0 && (
                    <>
                      <div className={classes.sectionLabel}>
                        {approvedLabel || 'Approved'}
                      </div>
                      <div className={classes.chipGroup}>
                        {approvedChips!.map((chip, i) => (
                          <HeatmapChip
                            key={chip.chipId}
                            chipId={chip.chipId}
                            index={i}
                            name={chip.name}
                            score={chip.score}
                            assigned={chip.assigned}
                            onRemove={noop}
                            onHover={onChipHover}
                            tooltipData={chip.tooltipData}
                            hideScore={hideChipScores}
                            locked
                          />
                        ))}
                      </div>
                    </>
                  )}
                  {assignedChips.length > 0 && (
                    <>
                      <div className={classes.sectionLabel}>
                        {assignedLabel}
                      </div>
                      <div className={classes.chipGroup}>
                        {assignedChips.map((chip, i) => (
                          <HeatmapChip
                            key={chip.chipId}
                            chipId={chip.chipId}
                            index={approvedCount + i}
                            name={chip.name}
                            score={chip.score}
                            assigned={chip.assigned}
                            onRemove={onRemoveChip}
                            onHover={onChipHover}
                            tooltipData={chip.tooltipData}
                            hideScore={hideChipScores}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  {candidateChips.length > 0 && (
                    <>
                      <div className={classes.sectionLabel}>
                        {candidateLabel}
                      </div>
                      <div className={classes.chipGroup}>
                        {candidateChips.map((chip, i) => (
                          <HeatmapChip
                            key={chip.chipId}
                            chipId={chip.chipId}
                            index={approvedCount + assignedChips.length + i}
                            name={chip.name}
                            score={chip.score}
                            assigned={chip.assigned}
                            onRemove={onRemoveChip}
                            onHover={onChipHover}
                            tooltipData={chip.tooltipData}
                            hideScore={hideChipScores}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
              {provided.placeholder}
            </div>
          </div>
        );
      }}
    </Droppable>
  );
});

export default HeatmapCard;
